import type { AxiosProgressEvent } from 'axios';
import { getBaseUrl, request } from 'src/helper/api';

export const getWorkOrders = (token?: string, signal?: AbortSignal) =>
  request(
    {
      method: 'GET',
      url: `${getBaseUrl()}api/work-orders`,
      contentType: 'json',
      token,
      signal,
    },
    'work-order/getWorkOrders',
  );

// work order details api
export const getWorkOrderDetails = (token?: string, workOrderId?: string, signal?: AbortSignal) =>
  request(
    {
      method: 'GET',
      url: `${getBaseUrl()}api/work-orders/${workOrderId}`,
      contentType: 'json',
      token,
      signal,
    },
    'work-order/getWorkOrderDetails',
  );

export const updateWorkOrderDescription = (token?: string, workOrderId?: string) =>
  request(
    {
      method: 'PATCH',
      url: `${getBaseUrl()}api/work-orders/${workOrderId}/description`,
      contentType: 'json',
      token,
    },
    'work-order/updateWorkOrderDescription',
  );
export const updateWorkOrderAccessMethod = (workOrderId?: string, token?: string) =>
  request(
    {
      method: 'PATCH',
      url: `${getBaseUrl()}api/work-orders/${workOrderId}/access-method`,
      contentType: 'json',
      token,
    },
    'work-order/updateWorkOrderAccessMethod',
  );
export const updateWorkOrderDueDate = (token?: string, workOrderId?: string) =>
  request(
    {
      method: 'PATCH',
      url: `${getBaseUrl()}api/work-orders/${workOrderId}/due-date`,
      contentType: 'json',
      token,
    },
    'work-order/updateWorkOrderDueDate',
  );
export const updateWorkOrderPriority = (token?: string, workOrderId?: string) =>
  request(
    {
      method: 'PATCH',
      url: `${getBaseUrl()}api/work-orders/${workOrderId}/priority`,
      contentType: 'json',
      token,
    },
    'work-order/updateWorkOrderPriority',
  );
export const updateWorkOrderProblemCategory = (token?: string, workOrderId?: string) =>
  request(
    {
      method: 'PATCH',
      url: `${getBaseUrl()}api/work-orders/${workOrderId}/problem-category`,
      contentType: 'json',
      token,
    },
    'work-order/updateWorkOrderProblemCategory',
  );
export const addWorkOrderProblemCategory = (token?: string, workOrderId?: string) =>
  request(
    {
      method: 'POST',
      url: `${getBaseUrl()}api/work-orders/${workOrderId}/problem-category`,
      contentType: 'json',
      token,
    },
    'work-order/addWorkOrderProblemCategory',
  );
export const updateWorkOrderPropertyAddress = (workOrderId?: string, token?: string) =>
  request(
    {
      method: 'PUT',
      url: `${getBaseUrl()}api/work-orders/${workOrderId}/property-address`,
      contentType: 'json',
      token,
      shouldAddSocketId: true,
    },
    'work-order/updateWorkOrderPropertyAddress',
  );

export const updateWorkOrderNteAmount = (
  token?: string,
  workOrderId?: string,
  prevNteAmount?: number,
  prevIsAiRecommended?: boolean,
) =>
  request(
    {
      method: 'PUT',
      url: `${getBaseUrl()}api/work-orders/${workOrderId}/actions/updateNteAmount`,
      contentType: 'json',
      token,
    },
    'work-order/updateWorkOrderNteAmount',
    // Carry the target work-order id into the fulfilled/rejected payload so the
    // reducer keys the optimistic-update rollback off the WO the request was for,
    // not whatever side panel happens to be open when the response lands (COM-715).
    { prevNteAmount, prevIsAiRecommended, workOrderId },
  );

export const updateWorkOrderResidentInfo = (token?: string, workOrderId?: string) =>
  request(
    {
      method: 'PUT',
      url: `${getBaseUrl()}api/work-orders/${workOrderId}/resident-info`,
      contentType: 'json',
      token,
      shouldAddSocketId: true,
    },
    'work-order/updateWorkOrderResidentInfo',
  );

// cancel work order

export const cancelWorkOrder = (workOrderId?: string, token?: string) =>
  request(
    {
      method: 'POST',
      url: `${getBaseUrl()}api/work-orders/${workOrderId}/actions/cancelWorkOrder`,
      contentType: 'json',
      token,
    },
    'work-order/cancelWorkOrder',
  );
export const awaitingAvailabilityWorkOrder = (
  workOrderId?: string,
  workOrderTaskId?: string,
  token?: string,
) =>
  request(
    {
      method: 'POST',
      url: `${getBaseUrl()}api/work-orders/${workOrderId}/tasks/${workOrderTaskId}/actions/awaitingAvailabilityWorkOrder`,
      contentType: 'json',
      token,
    },
    'work-order/awaitingAvailabilityWorkOrder',
  );

export const uploadMediaThumbnail = (workOrderId: string, token?: string, signal?: AbortSignal) =>
  request(
    {
      method: 'POST',
      url: `${getBaseUrl()}api/work-orders/${workOrderId}/media/thumbnail`,
      contentType: 'form-data',
      token,
      signal,
    },
    'work-order/uploadMediaThumbnail',
  );
export const uploadWorkOrderMedia = (
  woId: string,
  mediaId: string,
  token?: string,
  signal?: AbortSignal,
  onUploadProgress?: (pct: number) => void,
) =>
  request(
    {
      method: 'POST',
      url: `${getBaseUrl()}api/work-orders/${woId}/media/${mediaId}/upload`,
      contentType: 'form-data',
      token,
      signal,
      onUploadProgress: onUploadProgress
        ? (e: AxiosProgressEvent) => {
            if (e.total) onUploadProgress(Math.round((e.loaded / e.total) * 100));
          }
        : undefined,
    },
    'work-order/uploadWorkOrderMedia',
  );
export const uploadMedia = (token?: string, signal?: AbortSignal) =>
  request(
    {
      method: 'POST',
      url: `${getBaseUrl()}api/media-upload`,
      contentType: 'form-data',
      token,
      signal,
    },
    'media/upload',
  );

export const uploadWoMedia = (woId: string, token?: string, signal?: AbortSignal) =>
  request(
    {
      method: 'POST',
      url: `${getBaseUrl()}api/work-orders/${woId}/media/upload`,
      contentType: 'form-data',
      token,
      signal,
    },
    'work-order/uploadWoMedia',
  );
export const deleteWorkOrderMedia = (
  token?: string,
  work_order_uuid?: string,
  media_uuid?: string,
) =>
  request(
    {
      method: 'DELETE',
      url: `${getBaseUrl()}api/work-orders/${work_order_uuid}/media/${media_uuid}`,
      contentType: 'json',
      token,
    },
    'work-order/deleteWorkOrderMedia',
  );

export const deleteWorkOrderCategory = (token?: string, work_order_uuid?: string) =>
  request(
    {
      method: 'DELETE',
      url: `${getBaseUrl()}api/work-orders/${work_order_uuid}/problem-category`,
      contentType: 'json',
      token,
    },
    'work-order/deleteWorkOrderCategory',
  );

export const scheduleToVendor = (token?: string, workOrderId?: string) =>
  request(
    {
      method: 'POST',
      url: `${getBaseUrl()}api/work-orders/${workOrderId}/actions/scheduleToVendor`,
      contentType: 'json',
      token,
      shouldAddSocketId: true,
    },
    'work-order/scheduleToVendor',
  );

export const updateWorkOrderToVendor = (
  token?: string,
  workOrderId?: string,
  workOrderTaskId?: string,
) =>
  request(
    {
      method: 'POST',
      url: `${getBaseUrl()}api/work-orders/${workOrderId}/tasks/${workOrderTaskId}/actions/updateWorkOrderToVendor`,
      contentType: 'json',
      token,
    },
    'work-order/updateWorkOrderToVendor',
  );

export const scheduleWorkOrder = (token?: string, workOrderId?: string) =>
  request(
    {
      method: 'POST',
      url: `${getBaseUrl()}api/work-orders/${workOrderId}/actions/scheduleWorkOrder`,
      contentType: 'json',
      token,
      shouldAddSocketId: true,
    },
    'work-order/scheduleWorkOrder',
  );

export const rescheduleWorkOrderTrip = (token?: string, workOrderId?: string) =>
  request(
    {
      method: 'PUT',
      url: `${getBaseUrl()}api/work-orders/${workOrderId}/actions/reschedule`,
      contentType: 'json',
      token,
      shouldAddSocketId: true,
    },
    'work-order/rescheduleWorkOrderTrip',
  );

export const reScheduleWorkOrder = (token?: string, workOrderId?: string) =>
  request(
    {
      method: 'POST',
      url: `${getBaseUrl()}api/work-orders/${workOrderId}/actions/rescheduleWorkOrder`,
      contentType: 'json',
      token,
    },
    'work-order/reScheduleWorkOrder',
  );

export const pauseWorkOrder = (token?: string, workOrderId?: string, workOrderTaskId?: string) =>
  request(
    {
      method: 'POST',
      url: `${getBaseUrl()}api/work-orders/${workOrderId}/tasks/${workOrderTaskId}/actions/pauseWorkOrder`,
      contentType: 'json',
      token,
    },
    'work-order/manuallyPauseWorkOrder',
  );

export const pauseWorkInTrip = (token?: string, workOrderId?: string) =>
  request(
    {
      method: 'POST',
      url: `${getBaseUrl()}api/work-orders/${workOrderId}/actions/pauseTimer`,
      contentType: 'json',
      token,
    },
    'work-order/pauseWorkInTrip',
  );

export const resumeWorkInTrip = (token?: string, workOrderId?: string) =>
  request(
    {
      method: 'POST',
      url: `${getBaseUrl()}api/work-orders/${workOrderId}/actions/resumeWork`,
      contentType: 'json',
      token,
    },
    'work-order/resumeWorkInTrip',
  );

export const completeWorkOrder = (token?: string, workOrderId?: string, workOrderTaskId?: string) =>
  request(
    {
      method: 'POST',
      url: `${getBaseUrl()}api/work-orders/${workOrderId}/tasks/${workOrderTaskId}/actions/completeWorkOrder`,
      contentType: 'json',
      token,
    },
    'work-order/completeWorkOrder',
  );
export const startWorkInTrip = (token?: string, workOrderId?: string) =>
  request(
    {
      method: 'POST',
      url: `${getBaseUrl()}api/work-orders/${workOrderId}/actions/startWork`,
      contentType: 'json',
      token,
    },
    'work-order/startWorkInTrip',
  );
export const enRouteWorkOrder = (token?: string, workOrderId?: string) =>
  request(
    {
      method: 'PATCH',
      url: `${getBaseUrl()}api/work-orders/${workOrderId}/actions/enRoute`,
      contentType: 'json',
      token,
    },
    'work-order/enRouteWorkOrder',
  );
export const pauseEnRouteWorkOrder = (token?: string, workOrderId?: string) =>
  request(
    {
      method: 'POST',
      url: `${getBaseUrl()}api/work-orders/${workOrderId}/actions/pauseEnRoute`,
      contentType: 'json',
      token,
    },
    'work-order/pauseEnRouteWorkOrder',
  );
export const resumeEnRouteWorkOrder = (token?: string, workOrderId?: string) =>
  request(
    {
      method: 'POST',
      url: `${getBaseUrl()}api/work-orders/${workOrderId}/actions/resumeEnRoute`,
      contentType: 'json',
      token,
    },
    'work-order/resumeEnRouteWorkOrder',
  );
export const readyToScheduleWorkOrder = (token?: string, workOrderId?: string) =>
  request(
    {
      method: 'PATCH',
      url: `${getBaseUrl()}api/work-orders/${workOrderId}/actions/readyToScheduleWorkOrder`,
      contentType: 'json',
      token,
    },
    'work-order/readyToScheduleWorkOrder',
  );
// ORI-777: required NAMED params, unlike the positional `(token?, workOrderId?)` siblings above. Both
// values are `string`, so a positional signature lets `recallWorkOrder(workOrderId, token)` — an argument
// swap — typecheck silently and target the wrong URL/auth. Naming them makes the swap a compile error.
export const recallWorkOrder = ({ token, workOrderId }: { token: string; workOrderId: string }) =>
  request(
    {
      method: 'PATCH',
      url: `${getBaseUrl()}api/work-orders/${workOrderId}/actions/recallWorkOrder`,
      contentType: 'json',
      token,
    },
    'work-order/recallWorkOrder',
  );
export const resolveWorkOrder = (token?: string, workOrderId?: string, workOrderTaskId?: string) =>
  request(
    {
      method: 'POST',
      url: `${getBaseUrl()}api/work-orders/${workOrderId}/tasks/${workOrderTaskId}/actions/resolveWorkOrder`,
      contentType: 'json',
      token,
    },
    'work-order/resolveWorkOrder',
  );

export const reOpenWorkOrder = (token?: string, workOrderId?: string, workOrderTaskId?: string) =>
  request(
    {
      method: 'POST',
      url: `${getBaseUrl()}api/work-orders/${workOrderId}/tasks/${workOrderTaskId}/actions/reOpenWorkOrder`,
      contentType: 'json',
      token,
    },
    'work-order/reOpenWorkOrder',
  );

export const addAssignee = (
  token?: string,
  workOrderId?: string,
  userId?: string,
  signal?: AbortSignal,
) =>
  request(
    {
      method: 'POST',
      url: `${getBaseUrl()}api/work-orders/${workOrderId}/assignee/${userId}/add`,
      contentType: 'json',
      token,
      signal,
      shouldAddSocketId: true,
    },
    'work-order/addAssignee',
  );

export const removeAssignee = (
  token?: string,
  workOrderId?: string,
  userId?: string,
  signal?: AbortSignal,
) =>
  request(
    {
      method: 'DELETE',
      url: `${getBaseUrl()}api/work-orders/${workOrderId}/assignee/${userId}`,
      contentType: 'json',
      token,
      signal,
      shouldAddSocketId: true,
    },
    'work-order/removeAssignee',
  );
export const updateWorkOrderTrip = (
  token?: string,
  workOrderId?: string,
  workOrderTaskId?: string,
  tripId?: string,
) =>
  request(
    {
      method: 'PUT',
      url: `${getBaseUrl()}api/work-orders/${workOrderId}/tasks/${workOrderTaskId}/trips/${tripId}/actions/UpdateWorkOrderTrip`,
      contentType: 'json',
      token,
    },
    'work-order/updateWorkOrderTrip',
  );

export const readyToInvoiceWorkOrder = (
  token?: string,
  workOrderId?: string,
  workOrderTaskId?: string,
  signal?: AbortSignal,
) =>
  request(
    {
      method: 'POST',
      url: `${getBaseUrl()}api/work-orders/${workOrderId}/tasks/${workOrderTaskId}/actions/readyToInvoiceWorkOrder`,
      contentType: 'json',
      token,
      signal,
    },
    'work-order/readyToInvoiceWorkOrder',
  );

// invoice
export const getInvoiceSummary = (token?: string, workOrderId?: string) =>
  request(
    {
      method: 'GET',
      url: `${getBaseUrl()}api/work-orders/${workOrderId}/invoice/summary`,
      contentType: 'json',
      token,
    },
    'work-order/getInvoiceSummary',
  );

export const getInvoiceAfterCreate = (token?: string, workOrderId?: string, invoice_id?: string) =>
  request(
    {
      method: 'GET',
      url: `${getBaseUrl()}api/work-orders/${workOrderId}/invoices/${invoice_id}`,
      contentType: 'json',
      token,
    },
    'work-order/getInvoiceAfterCreate',
  );

export const getInvoiceList = (token?: string, workOrderId?: string) =>
  request(
    {
      method: 'GET',
      url: `${getBaseUrl()}api/work-orders/${workOrderId}/invoices`,
      contentType: 'json',
      token,
    },
    'work-order/getInvoiceList',
  );

export const createAndUpdateInvoice = (token?: string, workOrderId?: string) =>
  request(
    {
      method: 'POST',
      url: `${getBaseUrl()}api/work-orders/${workOrderId}/invoices/actions/invoicing`,
      contentType: 'json',
      token,
      // shouldAddSocketId: true,
    },
    'work-order/createAndUpdateInvoice',
  );

export const voidInvoice = (token?: string, workOrderId?: string, invoiceId?: string) =>
  request(
    {
      method: 'PUT',
      url: `${getBaseUrl()}api/work-orders/${workOrderId}/invoices/${invoiceId}/actions/voidInvoice`,
      contentType: 'json',
      token,
    },
    'work-order/voidInvoice',
  );
export const deleteInvoice = (token?: string, workOrderId?: string, invoiceId?: string) =>
  request(
    {
      method: 'DELETE',
      url: `${getBaseUrl()}api/work-orders/${workOrderId}/invoices/${invoiceId}/actions/deleteInvoice`,
      contentType: 'json',
      token,
    },
    'work-order/deleteInvoice',
  );
export const invoicePartiallyPaid = (token?: string, workOrderId?: string, invoiceId?: string) =>
  request(
    {
      method: 'PUT',
      url: `${getBaseUrl()}api/work-orders/${workOrderId}/invoices/${invoiceId}/actions/partiallyPaidInvoice`,
      contentType: 'json',
      token,
    },
    'work-order/partiallyPaidInvoice',
  );
export const fullyPaidInvoice = (token?: string, workOrderId?: string, invoiceId?: string) =>
  request(
    {
      method: 'PUT',
      url: `${getBaseUrl()}api/work-orders/${workOrderId}/invoices/${invoiceId}/actions/fullyPaidInvoice`,
      contentType: 'json',
      token,
    },
    'work-order/fullyPaidInvoice',
  );

// tags
export const assignTagToWorkerOrder = (
  token?: string,
  workOrderId?: string,
  signal?: AbortSignal,
) =>
  request(
    {
      method: 'POST',
      url: `${getBaseUrl()}api/work-orders/${workOrderId}/actions/applyTags`,
      contentType: 'json',
      token,
      signal,
      shouldAddSocketId: true,
    },
    'work-order/assignTags',
  );

export const createTag = (token?: string, signal?: AbortSignal) =>
  request(
    {
      method: 'POST',
      url: `${getBaseUrl()}api/tags`,
      contentType: 'json',
      token,
      signal,
      shouldAddSocketId: true,
    },
    'work-order/createTag',
  );

export const getAllTags = (token?: string, signal?: AbortSignal) =>
  request(
    {
      method: 'GET',
      url: `${getBaseUrl()}api/tags`,
      contentType: 'json',
      token,
      signal,
      shouldAddSocketId: true,
    },
    'work-order/getAllTags',
  );

export const deleteTag = (token?: string, tagId?: string) =>
  request(
    {
      method: 'DELETE',
      url: `${getBaseUrl()}api/tags/${tagId}`,
      contentType: 'json',
      token,
      shouldAddSocketId: true,
    },
    'work-order/deleteTag',
  );

export const updateTag = (token?: string, tagId?: string) =>
  request(
    {
      method: 'PUT',
      url: `${getBaseUrl()}api/tags/${tagId}`,
      contentType: 'json',
      token,
      shouldAddSocketId: true,
    },
    'work-order/updateTag',
  );

export const availabilityViewed = (token?: string, workOrderId?: string) =>
  request(
    {
      method: 'PATCH',
      url: `${getBaseUrl()}api/work-orders/${workOrderId}/availability-viewed`,
      contentType: 'json',
      token,
    },
    'work-order/availabilityViewed',
  );

export const deleteWorkOrderDueDate = (token?: string, workOrderId?: string) =>
  request(
    {
      method: 'DELETE',
      url: `${getBaseUrl()}api/work-orders/${workOrderId}/due-date`,
      contentType: 'json',
      token,
    },
    'work-order/deleteWorkOrderDueDate',
  );
export const deleteWorkOrder = (token?: string, workOrderId?: string) =>
  request(
    {
      method: 'DELETE',
      url: `${getBaseUrl()}api/work-orders/${workOrderId}/actions/deleteWorkOrder`,
      contentType: 'json',
      token,
    },
    'work-order/deleteWorkOrder',
  );

export const getScheduleContext = (token?: string, workOrderId?: string) =>
  request(
    {
      method: 'GET',
      url: `${getBaseUrl()}api/work-orders/${workOrderId}/schedule/context`,
      contentType: 'json',
      token,
    },
    'work-order/getScheduleContext',
    { workOrderId },
  );

export const getScheduleContextVendors = (
  token?: string,
  workOrderId?: string,
  signal?: AbortSignal,
) =>
  request(
    {
      method: 'GET',
      url: `${getBaseUrl()}api/work-orders/${workOrderId}/schedule/context/vendors`,
      contentType: 'json',
      token,
      signal,
    },
    'work-order/getScheduleContextVendors',
  );

export const getScheduleContextTechnicians = (token?: string, workOrderId?: string) =>
  request(
    {
      method: 'GET',
      url: `${getBaseUrl()}api/work-orders/${workOrderId}/schedule/technicians`,
      contentType: 'json',
      token,
    },
    'work-order/getScheduleContextTechnicians',
    { workOrderId },
  );

export const getTechnicianTimeSlots = (token?: string, workOrderId?: string, technician?: string) =>
  request(
    {
      method: 'GET',
      url: `${getBaseUrl()}api/work-orders/${workOrderId}/schedule/technicians/${technician}`,
      contentType: 'json',
      token,
    },
    'work-order/getTechnicianTimeSlots',
  );

export const getVendorAvailability = (token?: string, workOrderId?: string, vendorId?: string) =>
  request(
    {
      method: 'GET',
      url: `${getBaseUrl()}api/work-orders/${workOrderId}/schedule/vendor/${vendorId}/availability`,
      contentType: 'json',
      token,
    },
    'work-order/getVendorAvailability',
  );

export const createQuote = (token?: string, workOrderId?: string) =>
  request(
    {
      method: 'POST',
      url: `${getBaseUrl()}api/work-orders/${workOrderId}/actions/addQuote`,
      contentType: 'json',
      token,
      shouldAddSocketId: true,
    },
    'work-order/addQuote',
  );

// get owner quote details
export const getOwnerQuoteDetails = (quoteId?: string) =>
  request(
    {
      method: 'GET',
      url: `${getBaseUrl()}api/public/quotes/${quoteId}`,
      contentType: 'json',
    },
    'getOwnerQuoteDetails',
  );

/**
 * Re-reads the same public quote endpoint as getOwnerQuoteDetails, deliberately
 * as a SEPARATE thunk.
 *
 * getOwnerQuoteDetails' pending case sets `isFetchingQuoteDetails`, which the
 * approval page turns into a full-page skeleton — unmounting the form and
 * silently wiping every selection the owner made. Its rejected path is worse
 * still: the page's .catch turns a failed refetch into the fallback page,
 * destroying a perfectly good loaded quote. Neither is acceptable for a
 * background revalidation, and neither can be changed without altering the
 * initial-load behaviour. A distinct action type is what buys the different
 * reducer handling.
 *
 * No token: this is a public capability URL and the caller is unauthenticated.
 */
export const revalidateOwnerQuoteDetails = (quoteId?: string, signal?: AbortController['signal']) =>
  request(
    {
      method: 'GET',
      url: `${getBaseUrl()}api/public/quotes/${quoteId}`,
      contentType: 'json',
      signal,
    },
    'revalidateOwnerQuoteDetails',
  );

export const getQuoteDetails = (
  token?: string,
  quoteId?: string,
  signal?: AbortController['signal'],
) =>
  request(
    {
      method: 'GET',
      url: `${getBaseUrl()}api/quotes/${quoteId}`,
      contentType: 'json',
      token,
      signal,
    },
    'getQuoteDetails',
  );

export const ownerQuoteApprove = (workOrderId?: string, quoteId?: string) =>
  request(
    {
      method: 'POST',
      url: `${getBaseUrl()}api/work-orders/${workOrderId}/quotes/${quoteId}/actions/ownerApproveQuote`,
      contentType: 'json',
    },
    'ownerQuoteApprove',
  );

export const ownerQuoteReject = (workOrderId?: string, quoteId?: string) =>
  request(
    {
      method: 'POST',
      url: `${getBaseUrl()}api/work-orders/${workOrderId}/quotes/${quoteId}/actions/ownerRejectQuote`,
      contentType: 'json',
    },
    'ownerQuoteReject',
  );
export const coordinatorQuoteReject = (token?: string, workOrderId?: string, quoteId?: string) =>
  request(
    {
      method: 'POST',
      url: `${getBaseUrl()}api/work-orders/${workOrderId}/quotes/${quoteId}/actions/rejectQuote`,
      contentType: 'json',
      token,
    },
    'coordinatorQuoteReject',
    { workOrderId },
  );

export const restoreQuote = (token?: string, workOrderId?: string, quoteId?: string) =>
  request(
    {
      method: 'POST',
      url: `${getBaseUrl()}api/work-orders/${workOrderId}/quotes/${quoteId}/actions/restoreRejectedQuote`,
      contentType: 'json',
      token,
    },
    'restoreQuote',
    { workOrderId },
  );

export const editQuote = (
  token?: string,
  workOrderId?: string,
  quoteId?: string,
  shouldUpdateSource?: boolean,
) =>
  request(
    {
      method: 'POST',
      url: `${getBaseUrl()}api/work-orders/${workOrderId}/quotes/${quoteId}/actions/editQuote`,
      contentType: 'json',
      token,
      shouldAddSocketId: true,
    },
    'work-order/editQuote',
    { workOrderId, shouldUpdateSource },
  );

export const coordinatorQuoteApprove = (token?: string, workOrderId?: string, quoteId?: string) =>
  request(
    {
      method: 'POST',
      url: `${getBaseUrl()}api/work-orders/${workOrderId}/quotes/${quoteId}/actions/approveQuote`,
      contentType: 'json',
      token,
      shouldAddSocketId: true,
    },
    'coordinatorQuoteApprove',
    { workOrderId },
  );

export const sendForCustomerApproval = (token?: string, workOrderId?: string, quoteId?: string) =>
  request(
    {
      method: 'POST',
      url: `${getBaseUrl()}api/work-orders/${workOrderId}/quotes/${quoteId}/actions/sendForCustomerApproval`,
      contentType: 'json',
      token,
    },
    'sendForCustomerApproval',
    { workOrderId },
  );

export const fetchQuoteHistoryApi = (token?: string, quoteId?: string) =>
  request(
    {
      method: 'GET',
      url: `${getBaseUrl()}api/quotes/${quoteId}/history`,
      contentType: 'json',
      token,
    },
    'fetchQuoteHistoryApi',
  );

export const dispatchToLula = (token?: string, workOrderId?: string) =>
  request(
    {
      method: 'POST',
      url: `${getBaseUrl()}api/work-orders/${workOrderId}/actions/scheduleToVendor`,
      contentType: 'json',
      token,
      shouldAddSocketId: true,
    },
    'work-order/dispatchToLula',
  );

export const createQuoteTask = (token?: string, workOrderId?: string, quoteId?: string) =>
  request(
    {
      method: 'POST',
      url: `${getBaseUrl()}api/work-orders/${workOrderId}/quotes/${quoteId}/actions/addQuoteTask`,
      contentType: 'json',
      token,
      shouldAddSocketId: true,
    },
    'work-order/addQuoteTask',
  );

export const markAsDoneIssue = (token?: string, workOrderId?: string, issueId?: string) =>
  request(
    {
      method: 'POST',
      url: `${getBaseUrl()}api/work-orders/${workOrderId}/issues/${issueId}/actions/markAsDone`,
      contentType: 'json',
      token,
    },
    'work-order/markAsDoneIssue',
  );

export const markAsPendingIssue = (token?: string, workOrderId?: string, issueId?: string) =>
  request(
    {
      method: 'POST',
      url: `${getBaseUrl()}api/work-orders/${workOrderId}/issues/${issueId}/actions/markAsPending`,
      contentType: 'json',
      token,
    },
    'work-order/markAsPendingIssue',
  );

export const getIssueDetailsForCloseTrip = (
  token?: string,
  workOrderId?: string,
  issueId?: string,
  signal?: AbortController['signal'],
) =>
  request(
    {
      method: 'GET',
      url: `${getBaseUrl()}api/work-orders/${workOrderId}/issues/${issueId}`,
      contentType: 'json',
      token,
      signal,
    },
    'work-order/getIssueDetailsForCloseTrip',
  );

export const updateIssueDetailsForCloseTrip = (
  token?: string,
  workOrderId?: string,
  issueId?: string,
) =>
  request(
    {
      method: 'PUT',
      url: `${getBaseUrl()}api/work-orders/${workOrderId}/issues/${issueId}/actions/updateIssue`,
      contentType: 'json',
      token,
    },
    'work-order/updateIssueDetailsForCloseTrip',
  );

export const updateIssueDeclineReason = (token?: string, workOrderId?: string, issueId?: string) =>
  request(
    {
      method: 'PATCH',
      url: `${getBaseUrl()}api/work-orders/${workOrderId}/issues/${issueId}/actions/updateDeclineReason`,
      contentType: 'json',
      token,
    },
    'work-order/updateIssueDeclineReason',
  );

export const markAsUnresolvedIssue = (token?: string, workOrderId?: string, issueId?: string) =>
  request(
    {
      method: 'POST',
      url: `${getBaseUrl()}api/work-orders/${workOrderId}/issues/${issueId}/actions/decline`,
      contentType: 'json',
      token,
    },
    'work-order/markAsUnresolvedIssue',
  );

export const getCloseTripInfo = (token?: string, workOrderId?: string) =>
  request(
    {
      method: 'GET',
      url: `${getBaseUrl()}api/work-orders/${workOrderId}/close-trip-info`,
      contentType: 'json',
      token,
    },
    'work-order/getCloseTripInfo',
  );

export const closeTrip = (token?: string, workOrderId?: string) =>
  request(
    {
      method: 'POST',
      url: `${getBaseUrl()}api/work-orders/${workOrderId}/actions/close`,
      contentType: 'json',
      token,
    },
    'work-order/closeTrip',
  );

export const removeQuoteTask = (
  token?: string,
  workOrderId?: string,
  quoteId?: string,
  quoteTaskId?: string,
) =>
  request(
    {
      method: 'POST',
      url: `${getBaseUrl()}api/work-orders/${workOrderId}/quotes/${quoteId}/quote-tasks/${quoteTaskId}/actions/removeQuoteTask`,
      contentType: 'json',
      token,
    },
    'work-order/removeQuoteTask',
  );

export const updateQuoteTask = (
  token?: string,
  workOrderId?: string,
  quoteId?: string,
  quoteTaskId?: string,
) =>
  request(
    {
      method: 'PUT',
      url: `${getBaseUrl()}api/work-orders/${workOrderId}/quotes/${quoteId}/quote-tasks/${quoteTaskId}/actions/updateQuoteTask`,
      contentType: 'json',
      token,
    },
    'work-order/updateQuoteTask',
  );

export const updateDriveOrLaborTimeInCloseTrip = (
  workOrderId?: string,
  tripId?: string,
  token?: string,
) =>
  request(
    {
      method: 'PATCH',
      url: `${getBaseUrl()}api/work-orders/${workOrderId}/trip/${tripId}/actions/updateTripTime`,
      contentType: 'json',
      token,
    },
    'work-order/updateDriveOrLaborTimeInCloseTrip',
    { tripId },
  );

export const updateVendorLaborCloseTrip = (workOrderId?: string, tripId?: string, token?: string) =>
  request(
    {
      method: 'PATCH',
      url: `${getBaseUrl()}api/work-orders/${workOrderId}/trip/${tripId}/actions/updateTripCost`,
      contentType: 'json',
      token,
    },
    'work-order/updateVendorLaborCloseTrip',
  );

export const getCanelTripOptions = (token?: string, workOrderId?: string) =>
  request(
    {
      method: 'GET',
      url: `${getBaseUrl()}api/work-orders/${workOrderId}/cancel-trip-info`,
      contentType: 'json',
      token,
    },
    'work-order/getCanelTripOptions',
  );

export const cancelTrip = (token?: string, workOrderId?: string) =>
  request(
    {
      method: 'POST',
      url: `${getBaseUrl()}api/work-orders/${workOrderId}/actions/cancelTrip`,
      contentType: 'json',
      token,
    },
    'work-order/cancelTrip',
  );

export const approveVendorAdditionalAvailability = (
  token?: string,
  workOrderId?: string,
  availabilityRequestId?: string,
) =>
  request(
    {
      method: 'POST',
      url: `${getBaseUrl()}api/work-orders/${workOrderId}/request-availability/${availabilityRequestId}/approve`,
      contentType: 'json',
      token,
      shouldAddSocketId: true,
    },
    'work-order/approveVendorAdditionalAvailability',
  );

export const declineVendorAdditionalAvailability = (token?: string, workOrderId?: string) =>
  request(
    {
      method: 'POST',
      url: `${getBaseUrl()}api/work-orders/${workOrderId}/request-availability/decline`,
      contentType: 'json',
      token,
      shouldAddSocketId: true,
    },
    'work-order/declineVendorAdditionalAvailability',
  );

export const fetchSmartNteConfig = (token: string) =>
  request(
    {
      method: 'GET',
      url: `${getBaseUrl()}api/settings/work-orders/smart-nte`,
      contentType: 'json',
      token,
    },
    'work-order/fetchSmartNteConfig',
  );

export const updateSmartNteConfig = (token: string) =>
  request(
    {
      method: 'PUT',
      url: `${getBaseUrl()}api/settings/work-orders/smart-nte`,
      contentType: 'json',
      token,
    },
    'work-order/updateSmartNteConfig',
  );

export const getWorkOrdersForGroup = (
  token?: string,
  groupKey?: string,
  signal?: AbortSignal,
  attemptId?: number,
) =>
  request(
    {
      method: 'GET',
      url: `${getBaseUrl()}api/work-orders`,
      contentType: 'json',
      token,
      signal,
    },
    'work-order/getWorkOrdersForGroup',
    { _group_key: groupKey, _attempt: attemptId },
  );
